import { FC, ReactNode } from 'react';

import { cn } from 'support/helpers/cn/cn';

type SidebarProps = {
  side?: 'left' | 'right';
  children: ReactNode;
  bottomSlot?: ReactNode;
  width?: number | string;
  header?: ReactNode;
  overlay?: ReactNode;
  contentLayout?: 'padded' | 'flush';
  /**
   * When provided, wraps the content area with WAI-ARIA tabpanel semantics:
   * `role="tabpanel"`, `id="panel-{activeTabId}"`, and
   * `aria-labelledby="tab-{activeTabId}"`. Pair with `TabBar`, which emits
   * matching `id="tab-{id}"` and `aria-controls="panel-{id}"` on each button.
   */
  activeTabId?: string;
};

export const Sidebar: FC<SidebarProps> = ({
  children,
  bottomSlot,
  side = 'right',
  width = '360px',
  header,
  overlay,
  contentLayout = 'padded',
  activeTabId,
}) => {
  return (
    <aside
      className={cn(
        'relative flex flex-col justify-between border-gray-200',
        contentLayout === 'padded' && 'divide-y',
        side === 'left' ? 'border-r' : 'border-l',
      )}
      style={{ width, height: `var(--content-height)` }}
    >
      <div className="flex min-h-0 flex-1 flex-col">
        {overlay}
        {header}
        {contentLayout === 'padded' ? (
          <div
            role={activeTabId ? 'tabpanel' : undefined}
            id={activeTabId ? `panel-${activeTabId}` : undefined}
            aria-labelledby={activeTabId ? `tab-${activeTabId}` : undefined}
            className={cn(
              'min-h-0 flex-1 divide-y overflow-y-auto pb-5 *:px-5 *:pt-5 *:not-last:pb-5',
              !!header && '-mt-px',
            )}
          >
            {children}
          </div>
        ) : (
          <div
            role={activeTabId ? 'tabpanel' : undefined}
            id={activeTabId ? `panel-${activeTabId}` : undefined}
            aria-labelledby={activeTabId ? `tab-${activeTabId}` : undefined}
            className="flex min-h-0 flex-1 flex-col overflow-hidden"
          >
            {children}
          </div>
        )}
      </div>
      <div className="divide-y">
        <div className="divide-y py-3 *:px-6 *:not-last:pb-5 empty:hidden">{bottomSlot}</div>
      </div>
    </aside>
  );
};
